import { ElMessage } from "element-plus";

import {
  getMockUrl,
  getPrefixUrl,
  getOpenAccessTokenKey,
  getMockJwtByPrefixUrl,
  checkMallFetchUrl,
  checkShopFetchUrl,
  checkCollectionFetchUrl,
  getMallAppToken,
  getToken,
  getCurrentServer,
  setMallAppToken,
  setToken,
} from "../utils/globalData";
import { useMainStore } from "../composables/store";
import { batchTranslateTexts } from "../api/mall";
import {
  applyTransferTranslations,
  getHeaderValue,
  getCurrentLocaleTransferCode,
  resolveTransferRequestConfig,
  resolveCurrentTransferLanguage,
} from "../utils/transferTranslation";
import { get } from "lodash-es";
import { frontendErrorFetch } from "../utils/frontendErrorLog";
import { normalizeStorefrontShopData, normalizeStorefrontShopUrl, resolveStorefrontShopId } from "../utils/storefrontShop";

const responseMessageTranslationCache = new Map();

function normalizeShopContextValue(value) {
  return String(value || "").trim();
}

function isPlainRecord(value) {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function readCookieValue(name, headers = {}) {
  try {
    const cookieValue = useCookie(name, { path: "/" });
    if (cookieValue && cookieValue.value) {
      return cookieValue.value;
    }
  } catch (error) {
    // Fall through to request header parsing below.
  }

  if (headers.cookie && typeof headers.cookie === "string") {
    const match = headers.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
    if (match && match[1]) {
      try {
        return decodeURIComponent(match[1]);
      } catch (error) {
        return match[1];
      }
    }
  }

  return "";
}

function persistDealerShopIdCookie(value) {
  const normalizedDealerShopId = normalizeShopContextValue(value);
  if (!normalizedDealerShopId) {
    return;
  }

  try {
    const dealerShopIdCookie = useCookie("dealerShopId", { path: "/" });
    if (normalizeShopContextValue(dealerShopIdCookie.value) !== normalizedDealerShopId) {
      dealerShopIdCookie.value = normalizedDealerShopId;
    }
  } catch (error) {
    // Ignore cookie sync errors outside of an active Nuxt context.
  }
}

function resolveShopRequestDealerShopId({
  body,
  headers,
  hostSite,
  tokenKey,
  store,
  requestHeaders,
}) {
  const bodyParam = isPlainRecord(body?.param) ? body.param : {};
  const candidateList = [
    bodyParam.dealerShopId,
    body?.dealerShopId,
    bodyParam.shopId,
    body?.shopId,
    typeof store?.getDealerShopId === "function" ? store.getDealerShopId(hostSite) : "",
    typeof store?.getServerShopId === "function" ? store.getServerShopId(hostSite) : "",
    store?.$state?.dealerShopIdKeyMap?.[tokenKey] || "",
    typeof store?.getShopId === "function" ? store.getShopId(hostSite) : "",
    store?.$state?.shopIdKeyMap?.[tokenKey] || "",
    // An incoming SSR cookie can belong to the site's previous shop binding.
    readCookieValue("dealerShopId", headers),
    requestHeaders?.shopId,
    requestHeaders?.shopid,
  ];

  const resolvedDealerShopId = candidateList.find((item) =>
    normalizeShopContextValue(item)
  );

  return resolveStorefrontShopId(resolvedDealerShopId, hostSite);
}

function normalizeShopRequestBody(body, context) {
  body = normalizeStorefrontShopData(body, context.hostSite);
  if (!isPlainRecord(body)) {
    return body;
  }

  const resolvedDealerShopId = resolveShopRequestDealerShopId({
    ...context,
    body,
  });

  if (!resolvedDealerShopId) {
    return body;
  }

  persistDealerShopIdCookie(resolvedDealerShopId);

  if (isPlainRecord(body.param)) {
    if (normalizeShopContextValue(body.param.dealerShopId)) {
      return body;
    }

    return {
      ...body,
      param: {
        ...body.param,
        dealerShopId: resolvedDealerShopId,
      },
    };
  }

  if (!("param" in body) && !normalizeShopContextValue(body.dealerShopId)) {
    return {
      ...body,
      dealerShopId: resolvedDealerShopId,
    };
  }

  return body;
}

async function translateResponseMessage(message = "", authorization = "") {
  const normalizedMessage = String(message || "").trim();
  if (!process.client || !normalizedMessage) {
    return normalizedMessage;
  }

  const currentLocale = String(resolveCurrentTransferLanguage() || "").trim();
  if (!currentLocale || currentLocale.toLowerCase().startsWith("zh")) {
    return normalizedMessage;
  }

  const targetLanguage = getCurrentLocaleTransferCode(currentLocale);
  if (!targetLanguage) {
    return normalizedMessage;
  }

  const cacheKey = `${targetLanguage}__${normalizedMessage}`;
  if (responseMessageTranslationCache.has(cacheKey)) {
    return responseMessageTranslationCache.get(cacheKey);
  }

  try {
    const translateResponse = await batchTranslateTexts(
      {
        isSystemText: 0,
        texts: [normalizedMessage],
        sourceLanguage: "zh",
        targetLanguage,
        appType: "react-mall-app",
      },
      authorization
    );

    const translatedMessage = String(
      get(translateResponse, "data.results[0].translatedText") ||
        get(translateResponse, "data.data.results[0].translatedText") ||
        get(translateResponse, "data.result") ||
        normalizedMessage
    ).trim() || normalizedMessage;

    responseMessageTranslationCache.set(cacheKey, translatedMessage);
    return translatedMessage;
  } catch (error) {
    return normalizedMessage;
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  // Keep the store belonging to this SSR request across asynchronous calls.
  const store = useMainStore(nuxtApp.$pinia);
  const customFetch = async (url, options = {}) => {
    const headers = useRequestHeaders();
    const requestUrl = nuxtApp?.ssrContext?.event?.node?.req?.url || "";
    const hostSite = getCurrentServer(headers, requestUrl);
    url = normalizeStorefrontShopUrl(url, hostSite);
    const prefixUrl = getPrefixUrl(url, headers);
    const isShopUrl = checkShopFetchUrl(url);

    const tokenKey = getOpenAccessTokenKey(hostSite);

    // 设置默认请求头
    const defaultHeaders = {
      ...options.headers,
    };

    // 如果有 mock JWT 或 store 中的 token，将其添加到请求头
    const mockJwt = getMockJwtByPrefixUrl(prefixUrl);
    if (mockJwt) {
      defaultHeaders["openaccessjwt"] = mockJwt;
    }

    if (store && store.$state.tokenKeyMap[tokenKey]) {
      defaultHeaders["openaccessjwt"] = store.$state.tokenKeyMap[tokenKey];
    }
    
    if (store && store.$state.shopIdKeyMap[tokenKey]) {
      defaultHeaders["shopId"] = store.$state.shopIdKeyMap[tokenKey];
    }

    const normalizedBody = isShopUrl
      ? normalizeShopRequestBody(options.body, {
          headers,
          hostSite,
          tokenKey,
          store,
          requestHeaders: defaultHeaders,
        })
      : options.body;

    if (!defaultHeaders.shopId && !defaultHeaders.shopid) {
      const resolvedShopId = normalizeShopContextValue(
        (typeof store?.getShopId === "function" ? store.getShopId(hostSite) : "")
          || resolveShopRequestDealerShopId({
            body: normalizedBody,
            headers,
            hostSite,
            tokenKey,
            store,
            requestHeaders: defaultHeaders,
          })
      );

      if (resolvedShopId) {
        defaultHeaders["shopId"] = resolvedShopId;
      }
    }

    const transferRequestConfig = resolveTransferRequestConfig(
      defaultHeaders,
      normalizedBody
    );


    // 请求方法和参数处理
    let fetchOptions = {
      ...options,
      body: normalizedBody,
      headers: normalizeStorefrontShopData(defaultHeaders, hostSite),
      baseURL: prefixUrl,
    };

    if (process.client) {
      // 客户端登录信息管理
      let isShopUrl = checkShopFetchUrl(url);
      let isMallUrl = checkMallFetchUrl(url);
      let isCollectionUrl = checkCollectionFetchUrl(url);
      if(isShopUrl){
        const token = getToken();
        if (typeof fetchOptions.headers != "undefined") {
          if (typeof token === "string") {
            fetchOptions.headers.authorization = token;
          } else {
            fetchOptions.headers.authorization = "";
          }
        }
      } else if (isMallUrl) {
        fetchOptions.headers.authorization = getMallAppToken();
      } else if (isCollectionUrl) {
        const token = getToken();
        if (typeof fetchOptions.headers != "undefined") {
          if (typeof token === "string") {
            fetchOptions.headers.authorization = token;
          } else {
            fetchOptions.headers.authorization = "";
          }
        }
      }
    }

    try {
      // 发送请求
      const response = await frontendErrorFetch($fetch, url, fetchOptions);
      const resHeaders = response.headers;
      const authorizationData = getHeaderValue(resHeaders, "authorization");
      if (process.client && authorizationData) {
        // 客户端登录信息管理
        let isShopUrl = checkShopFetchUrl(url);
        let isMallUrl = checkMallFetchUrl(url);
        let isCollectionUrl = checkCollectionFetchUrl(url);
        if (isMallUrl) {
          setToken(authorizationData);
        } else if (isCollectionUrl) {
          setMallAppToken(authorizationData);
        }else if(isShopUrl){
          setToken(authorizationData);
      
        }
      }

      try{
        const mallAuthorization =
          fetchOptions.headers?.authorization || authorizationData || getMallAppToken();
        if (response._data) {
          await applyTransferTranslations({
            responseData: response._data,
            transferConfig: transferRequestConfig,
            mallAuthorization,
            translate: (payload) => batchTranslateTexts(payload, mallAuthorization),
          });

          if (typeof response._data.msg === "string" && response._data.msg.trim()) {
            response._data.msg = await translateResponseMessage(
              response._data.msg,
              mallAuthorization
            );
          }
        }
      }catch(e){
        console.log(e)
      }

      // 检查响应状态
      // if (
      //   !response.ok ||
      //   (typeof response._data.success == "boolean" && !response._data.success)
      // ) {
      //   throw new Error(response._data);
      // }

      // 处理响应头
      //  const resHeaders ={}// useResponseHeader(['openaccessjwt','tracking_query_meta'])

      // 如果响应中有新的 token，更新 store
      if (resHeaders.has("openaccessjwt")) {
        store.setTokenKeyMap(tokenKey, resHeaders.get("openaccessjwt"));
      }
 
      
      const responseShopId = resolveStorefrontShopId(
        get(response._data, 'data.configData.shopId'), hostSite
      );
      const responseDealerShopId = resolveStorefrontShopId(
        get(response._data, 'data.configData.dealerShopId') || get(response._data, 'data.dealerShopId'), hostSite
      );
      if (responseShopId) store.setShopIdKeyMap(tokenKey, responseShopId);
      if (responseDealerShopId) {
        store.setDealerShopIdKeyMap(tokenKey, responseDealerShopId);
        persistDealerShopIdCookie(responseDealerShopId);
      }


      // 如果有 tracking_query_meta，将其存储在 localStorage
      if (resHeaders.has("tracking_query_meta") && process.client) {
        window.localStorage.setItem(
          "tracking_query_meta",
          resHeaders.get("tracking_query_meta")
        );
      }

      // 返回响应数据
      return response._data;
    } catch (error) {
      // 错误处理
      console.error(error);
      const errorMessage = error.message || "请求失败";

      // 错误码处理
      if (error.message.includes("401")&&!url.match('uncheck401=1')) {
        if (process.client) {
          // 客户端登录信息管理
          let isShopUrl = checkShopFetchUrl(url);
          let isMallUrl = checkMallFetchUrl(url);
          let isCollectionUrl = checkCollectionFetchUrl(url);
          if ((isMallUrl || isCollectionUrl||isShopUrl)&&!url.match('uncheck401=1')) {
            ElMessage({
              showClose: true,
              message: "请先登录",
              type: "error",
            });
            store.logout();
          } else {
            ElMessage({
              showClose: true,
              message: errorMessage,
              type: "error",
            });
          }
        }
      } else if (error.message.includes("403")) {
        if (process.server) {
          navigateTo("/error/forbidden");
        }
      } else if (error.message.includes("404")) {
        if (process.server) {
          navigateTo("/error/not-found");
        }
      } else if (error.message.includes("500")) {
        if (process.client) {
          ElMessage({
            showClose: true,
            message: errorMessage,
            type: "error",
          });
        }
      }

      throw error;
    }
  };

  // 将 customFetch 提供给 Nuxt 应用
  nuxtApp.provide("customFetch", customFetch);
});
